// Gallery image input.
// Click the plus tile to add images. Every image (existing or newly added) is a
// GalleryImage tile that can be reordered by dragging its grip bar (or arrow
// keys on the focused grip), removed with the X, run through OCR to append
// its text to the description, or clicked to view full size. The full-size
// view offers "Use as preview image" when the parent passes `onUseAsPreview`.
//
// The list is owned by the parent: `onChange` receives the full new list on
// add/remove/reorder. Removing an existing image also fires `onRemoveExisting`
// so the parent can tell the API to delete it on save.

import { useRef, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import GalleryImage from "./GalleryImage";
import ImagePreviewModal from "../ImagePreviewModal";
import type { GalleryItem } from "./GalleryImage";
import { extractText } from "../../../js/ocr";

export type { GalleryItem };

type GalleryInputProps = {
  items: GalleryItem[];
  onChange: (items: GalleryItem[]) => void;
  onRemoveExisting?: (id: number) => void;
  onUseAsPreview?: (item: GalleryItem) => void;
  onOcrText: (text: string) => void;
  onError: (message: string) => void;
};

let newItemSeq = 0;

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

const GalleryInput = ({ items, onChange, onRemoveExisting, onUseAsPreview, onOcrText, onError }: GalleryInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragFrom = useRef<number | null>(null);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [ocrBusyKey, setOcrBusyKey] = useState<string | null>(null);
  const [viewing, setViewing] = useState<GalleryItem | null>(null);

  const onClickInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const added: GalleryItem[] = Array.from(e.target.files ?? []).map((file) => ({
      key: `new-${newItemSeq++}`,
      kind: "new",
      file,
      url: URL.createObjectURL(file),
    }));
    if (added.length) onChange([...items, ...added]);
    // allow picking the same file again later
    e.target.value = "";
  };

  const remove = (index: number) => {
    const item = items[index];
    if (item.kind === "new") {
      URL.revokeObjectURL(item.url);
    } else {
      onRemoveExisting?.(item.id);
    }
    onChange(items.filter((_, i) => i !== index));
  };

  const move = (from: number, to: number) => {
    const next = moveItem(items, from, to);
    if (next !== items) onChange(next);
  };

  const runOcr = async (item: GalleryItem) => {
    setOcrBusyKey(item.key);
    try {
      const text = await extractText(item.kind === "new" ? item.file : item.url);
      if (!text.trim()) {
        onError("No text was found in that image.");
        return;
      }
      onOcrText(text);
    } catch (err: any) {
      onError(err?.message || "Failed to extract text from the image.");
    } finally {
      setOcrBusyKey(null);
    }
  };

  const useAsPreview = () => {
    if (!viewing || !onUseAsPreview) return;
    onUseAsPreview(viewing);
    setViewing(null);
  };

  return (
    <>
      <Row className="gy-3" data-testid="gallery-input">
        <Col sm={2} className="col-6">
          <div className="thumbnailInput recipeImage" onClick={onClickInput} title="Add gallery images">
            <input
              name="gallery_images"
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            ></input>
            <PlusCircle className="recipeImageAddIcon" />
          </div>
        </Col>
        {items.map((item, index) => (
          <Col key={item.key} sm={2} className="col-6">
            <GalleryImage
              item={item}
              index={index}
              count={items.length}
              ocrBusy={ocrBusyKey === item.key}
              dragging={draggingKey === item.key}
              onRemove={() => remove(index)}
              onOcr={() => runOcr(item)}
              onView={() => setViewing(item)}
              onMove={(delta) => move(index, index + delta)}
              onDragStart={() => {
                dragFrom.current = index;
                setDraggingKey(item.key);
              }}
              onDragEnd={() => {
                dragFrom.current = null;
                setDraggingKey(null);
              }}
              onDrop={() => {
                if (dragFrom.current !== null) move(dragFrom.current, index);
                dragFrom.current = null;
                setDraggingKey(null);
              }}
            />
          </Col>
        ))}
      </Row>
      <ImagePreviewModal
        src={viewing?.url ?? null}
        alt="Gallery image, full size"
        onHide={() => setViewing(null)}
        actions={
          onUseAsPreview && (
            <Button variant="primary" type="button" onClick={useAsPreview}>
              Use as preview image
            </Button>
          )
        }
      />
    </>
  );
};

export default GalleryInput;
